/**
 * Entitlement — Bug #19757.
 *
 * "Signed in" and "entitled to XCentral" are different facts. Entra admits any account in the
 * tenant, so before this the portal collapsed every outcome of the roles lookup into
 * `roles: []` and rendered the app shell to people who hold no role at all.
 *
 * The four states, and what the UI does with each:
 *
 * - `loading`        — roles not resolved yet. Render nothing that decides access.
 * - `entitled`       — the caller holds at least one effective role. Normal shell; a page that needs a
 *                      role the user lacks still does its own page-level denial (they stay signed in).
 * - `noAccess`       — the effective-roles endpoint returned no roles, OR Entra refused the API token with
 *                      AADSTS50105 (user not assigned to the application). The Access denied screen.
 * - `transientError` — any other failure (network, timeout, interaction required). Deliberately NOT
 *                      a denial: a flaky network must never sign an authorised user out. Today's
 *                      behaviour (empty roles, shell rendered) is kept for this state.
 *
 * #21587 — roles now come from the API's `GET /api/v1/me/effective-roles` (Entra app roles plus this
 * environment's StaffRoles, after claims transformation; see ./effectiveRoles.ts), not from the access
 * token's `roles` claim. Any failure of that endpoint is `transientError`; the token claim is never a
 * fallback.
 */
export type Entitlement = 'loading' | 'entitled' | 'noAccess' | 'transientError';

/** Entra's "the signed-in user is not assigned to a role for the application" error code. */
export const NOT_ASSIGNED_ERROR_CODE = 'AADSTS50105';

/**
 * True when `error` is Entra's AADSTS50105 refusal.
 *
 * MSAL surfaces it in different fields depending on the path (a ServerError from the redirect
 * response, an InteractionRequiredAuthError from a refresh-token grant), so every string field
 * MSAL errors carry is checked. Anything that is not an object, or carries no such code, is false —
 * the default is "not a denial", which keeps a transient failure from ever showing Access denied.
 */
export function isNotAssignedError(error: unknown): boolean {
  if (error === null || typeof error !== 'object') return false;
  const e = error as Record<string, unknown>;
  for (const field of ['errorCode', 'subError', 'errorMessage', 'message', 'errorNo']) {
    const value = e[field];
    if (typeof value === 'string' && value.includes(NOT_ASSIGNED_ERROR_CODE)) return true;
    if (field === 'errorNo' && (value === 50105 || value === '50105')) return true;
  }
  return false;
}

/** Entitlement derived from a successful effective-roles response (#21587). */
export function entitlementFromRoles(roles: readonly string[]): Entitlement {
  return roles.length > 0 ? 'entitled' : 'noAccess';
}

/** Entitlement derived from a failed API-token request. */
export function entitlementFromError(error: unknown): Entitlement {
  return isNotAssignedError(error) ? 'noAccess' : 'transientError';
}
